import { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../ui/dialog";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../../ui/tooltip";

import {
    Shield,
    Key,
    Clock,
    Search,
    User,
    Settings,
    FileText,
    CheckCircle2,
    Loader2
} from "lucide-react";
import useUserStore from "../../../services/userStore";
import { Device } from "../interfaces/EndpointInterfaces";
import { submitForm, isLocalWindowsUser } from "./Api/tenantuserendpointsapi";
import { useToast } from "../../../hooks/use-toast";
import "../ManagementDialog.css";
import "./TenantEndpoints.css";

interface RequestConnectAccessDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmitSuccess?: () => void | Promise<void>;
    deviceName?: string;
    applicationName?: string;
    osType?: "windows" | "linux" | "manual";
    userList?: any[];
    sessionRequest?: boolean | false;
    resourceType?: string;
    resourcseId?: string;
    isWhitewanAccess?: string;
    isFromEndPoint?: boolean | false;
    isFromEndPointApp?: boolean | false;
    tenantName: string;
    device?: Device | null;
    publisherId: string;
    resourceName: string;
    resourcePath: string;
}

interface Credential {
    id: string;
    name: string;
    email: string;
    credentialLabel: string;
    credentialUsername: string;
}

export default function RequestConnectAccessDialog({
    open,
    onOpenChange,
    onSubmitSuccess,
    deviceName = "",
    applicationName = "",
    osType = "windows",
    userList,
    sessionRequest,
    resourceType,
    resourcseId,
    isWhitewanAccess,
    isFromEndPoint,
    isFromEndPointApp,
    tenantName,
    device,
    publisherId,
    resourceName,
    resourcePath
}: RequestConnectAccessDialogProps) {
    const [connectionMode, setConnectionMode] = useState("generic");
    const [accessType, setAccessType] = useState("DYNAMIC");
    const [scheduleAccess, setScheduleAccess] = useState(false);
    const [justification, setJustification] = useState("");
    const [timeMinutes, setTimeMinutes] = useState("");
    const [selectedCredential, setSelectedCredential] = useState<Credential>();
    const [step, setStep] = useState<"credentials" | "request">("request");
    const [credentialSearch, setCredentialSearch] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [credentials, setCredentials] = useState<Credential[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [jitDisabled, setJitDisabled] = useState(false);
    const [checkingLocalUser, setCheckingLocalUser] = useState(false);
    let { email, userId } = useUserStore((state) => state.user);
    const { toast } = useToast();

    const JIT_DISABLED_DISCLAIMER =
        "JIT User is unavailable because a local Windows account with your username already exists on this machine. Use Configured or Manual credentials instead.";

    const handleConnectionModeChange = (value: string) => {
        if (value === "generic" && jitDisabled) {
            return;
        }
        setConnectionMode(value);
        if (value === "existingCredentials") {
            setStep("credentials");
        } else {
            setStep("request");
        }
    };

    const isDeviceSSh = (device: Device) => {
        return (
            !device?.isManuallyAddedMachine &&
            (
                device?.OperatingSystem?.toLowerCase().includes("linux") ||
                device?.OperatingSystem?.toLowerCase().includes("macos")
            )
        );
    };

    useEffect(() => {
        if (open && (osType === "linux" || osType === "manual")) {
            setConnectionMode("existingCredentials");
            setStep("credentials");
        }

    }, [osType, open]);

    const applyJitDisabledSelection = (hasConfiguredCredentials: boolean) => {
        setConnectionMode("manualCredentials");
        setStep("request");
    };

    useEffect(() => {
        let cancelled = false;

        const checkLocalWindowsUser = async () => {
            if (!open || osType !== "windows" || !device || device.isManuallyAddedMachine || !tenantName || !email) {
                setJitDisabled(false);
                return;
            }

            setCheckingLocalUser(true);
            try {
                const result = await isLocalWindowsUser(tenantName, device.id, email);
                if (cancelled) return;
                const shouldDisableJit = !result.showJitTab;
                setJitDisabled(shouldDisableJit);
                if (shouldDisableJit) {
                    const hasConfiguredCredentials = (userList?.length ?? 0) > 0 || credentials.length > 0;
                    applyJitDisabledSelection(hasConfiguredCredentials);
                }
            } catch (error) {
                console.error("Failed to resolve Windows JIT visibility", error);
                if (!cancelled) {
                    setJitDisabled(false);
                }
            } finally {
                if (!cancelled) {
                    setCheckingLocalUser(false);
                }
            }
        };

        checkLocalWindowsUser();
        return () => {
            cancelled = true;
        };
    }, [open, osType, device, tenantName, email]);

    useEffect(() => {
        if (!open) {
            setJitDisabled(false);
            setCheckingLocalUser(false);
            setConnectionMode("generic");
            setStep("request");
            setSelectedCredential(undefined);
        }
    }, [open]);

    useEffect(() => {
        if (userList && userList.length > 0) {
            const creds = userList.map(device => ({
                id: device.id,
                name: device.cn,
                email: device.email,
                credentialUsername: device.credentialUsername || "",
                credentialLabel: device.credentialLabel || ""
            }));
            setCredentials(creds || []);
            if (jitDisabled && open && osType === "windows") {
                applyJitDisabledSelection(true);
            }
        } else if (userList) {
            setCredentials([]);
            if (jitDisabled && open && osType === "windows") {
                applyJitDisabledSelection(false);
            }
        }
    }, [userList, jitDisabled, open, osType]);


    const handleCredentialSelect = (credential: Credential) => {
        setSelectedCredential(credential);
        setStep("request");
    };

    const getStringBeforeAtSymbol = (str: string) => {
        const atIndex = str.indexOf("@");
        return atIndex !== -1 ? str.substring(0, atIndex) : str;
    };

    const isDeviceWindows = (device: Device): boolean => {
        if (device.OperatingSystem && device.OperatingSystem.trim().length > 0) {
            return device.OperatingSystem.toLowerCase().includes("windows");
        }
        return false;
    };

    const isServer = device?.belongedToUser
        ? device.belongedToUser === email
            ? false
            : device?.isServer
        : device?.isServer;

    const handleSubmit = async () => {
        if (isSubmitting || !device) {
            return;
        }

        setIsSubmitting(true);

        if (device) {
            const username = getStringBeforeAtSymbol(email);

            const scheduledStart = scheduleAccess && startDate ? new Date(startDate).toISOString() : null;
            const scheduledEnd = scheduleAccess && endDate ? new Date(endDate).toISOString() : null;

            const isConfiguredCredentialFlow = isDeviceWindows(device) || !!device?.isManuallyAddedMachine;

            const payload = {
                userId: sessionRequest ? userId : selectedCredential?.id,
                username: sessionRequest ? username : selectedCredential?.name,
                email:
                    isFromEndPointApp && isDeviceSSh(device)
                        ? email
                        : sessionRequest
                            ? email
                            : selectedCredential?.email,
                resourceType: sessionRequest ? "GROUP" : resourceType,
                resourceId: resourcseId,
                resourceName,
                computerName:
                    isFromEndPointApp && isDeviceSSh(device)
                        ? sessionRequest
                            ? email
                            : selectedCredential?.email
                        : device?.dNSHostName,
                friendlyName: device?.friendlyName,
                resourcePath: resourcePath || "",
                requestMessage: justification,
                requestStatus: "PENDING",
                selectedTime: accessType.toUpperCase() === "PERMANENT" ? 0 : scheduleAccess ? 0 : timeMinutes,
                computerId: device?.id,
                createdAt: new Date().toISOString(),
                privilegeLevel: accessType.toUpperCase(),
                tenantName,
                publisherId,
                userEmail: email,
                toConnect: sessionRequest ? true : false,
                isWhiteswanAccess: isWhitewanAccess,
                isServer,
                machineUser: selectedCredential?.name,
                machineUserId: selectedCredential?.id,
                connectionMode: isConfiguredCredentialFlow ? connectionMode : "",
                connectionUser: isConfiguredCredentialFlow ? selectedCredential?.credentialUsername : "",
                connectionLabel: isConfiguredCredentialFlow ? selectedCredential?.credentialLabel : "",
                type: "onPrem",
                timingMode: accessType.toUpperCase() === "PERMANENT" ? 0 : scheduleAccess ? "SCHEDULED" : "GENERIC",
                scheduledStart: accessType.toUpperCase() === "PERMANENT" ? null : scheduleAccess && scheduledStart,
                scheduledEnd: accessType.toUpperCase() === "PERMANENT" ? null : scheduleAccess && scheduledEnd,
            };

            try {
                await submitForm(payload);
                await onSubmitSuccess?.();

                toast({
                    title: "Request connect",
                    description: `Successfully raised the request for ${device.dNSHostName}`,
                });
            } catch (e: any) {
                toast({
                    title: "Request connect failed",
                    description:
                        e?.response?.data?.message ||
                        e?.message ||
                        `Unable to raise the request for ${device.dNSHostName}`,
                    variant: "destructive",
                });
                return;
            } finally {
                setIsSubmitting(false);
            }

            setCredentials([])
            onOpenChange(false);
            handleCancel()
        }

    };



    const handleBack = () => {
        if (step === "request" && connectionMode === "existingCredentials") {
            setStep("credentials");
        }
    };

    const handleCancel = () => {
        setSelectedCredential(undefined);
        onOpenChange(false);
        // Reset form
        setConnectionMode("generic");
        setAccessType("DYNAMIC");
        setScheduleAccess(false);
        setJustification("");
        setTimeMinutes("");
        setStep("request");
        setCredentialSearch("");
        setStartDate("");
        setEndDate("");
        setCredentials([])
    };

    useEffect(() => {
        if (!open) {
            setConnectionMode("generic");
            setAccessType("DYNAMIC");
            setScheduleAccess(false);
            setJustification("");
            setTimeMinutes("");
            setStep("request");
            setCredentialSearch("");
            setStartDate("");
            setEndDate("");
        }
    }, [open]);

    useEffect(() => {
        setSelectedCredential(undefined);
        setAccessType("DYNAMIC");
        setScheduleAccess(false);
        setJustification("");
        setTimeMinutes("");
        setCredentialSearch("");
        setStartDate("");
        setEndDate("");
    }, [connectionMode])


    useEffect(() => {
        if (accessType === "permanent") {
            setScheduleAccess(false);
            setStartDate("");
            setEndDate("");
        }
    }, [accessType]);


    // Filter credentials based on search
    const filteredCredentials = credentials?.filter(credential =>
        credential?.name?.toLowerCase().includes(credentialSearch.toLowerCase()) ||
        credential?.credentialUsername?.toLowerCase().includes(credentialSearch.toLowerCase())
    );

    if (step === "credentials" && connectionMode === "existingCredentials") {
        return (
            <Dialog open={open} onOpenChange={() => {
                setCredentials([])
                onOpenChange(false);
                handleCancel()
                onOpenChange(false)
            }} modal={true}>
                <DialogContent
                    className="management-dialog tenant-access-dialog max-w-xl"
                    style={{ overscrollBehavior: "contain" }}
                >
                    <DialogHeader className="management-dialog-header">
                        <div className="management-dialog-heading">
                            <span className="management-dialog-icon">
                                <Key aria-hidden="true" />
                            </span>
                            <div>
                                <DialogTitle>Select Credentials</DialogTitle>
                                <p className="text-muted-foreground">
                                    Choose the credentials to use for secure connection to <span className="font-medium text-foreground">{deviceName}</span>
                                </p>
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="management-dialog-body">
                        <section className="management-form-section">
                            <div className="tenant-access-search">
                                <Search aria-hidden="true" />
                                <Input
                                    placeholder="Search credentials by name, type, or description..."
                                    value={credentialSearch}
                                    onChange={(e) => setCredentialSearch(e.target.value)}
                                    data-testid="input-credential-search"
                                />
                            </div>
                            <div className="management-section-title">
                                <Shield aria-hidden="true" />
                                Available Credentials
                                <span className="tenant-access-count">{filteredCredentials.length}</span>
                            </div>
                            {filteredCredentials.length > 0 ? (
                                <div className="tenant-access-credentials">
                                    {filteredCredentials.map((credential) => (
                                        <button
                                            type="button"
                                            key={credential.id}
                                            className="tenant-access-credential"
                                            onClick={() => handleCredentialSelect(credential)}
                                            data-testid={`credential-${credential.id}`}
                                        >
                                            <span className="tenant-access-credential-icon">
                                                <User aria-hidden="true" />
                                            </span>
                                            <span className="tenant-access-credential-name">
                                                {credential.name || credential.credentialUsername}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <div className="tenant-access-empty">
                                    <Search aria-hidden="true" />
                                    <p>No credentials found matching your search.</p>
                                    <span>Try adjusting your search criteria.</span>
                                </div>
                            )}
                        </section>
                    </div>

                    <div className="management-dialog-footer">
                        <Button variant="outline" onClick={handleCancel} data-testid="button-cancel">
                            Cancel
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={() => {
            setCredentials([])
            onOpenChange(false);
            handleCancel()
            onOpenChange(false)
        }} modal={true}>
            <DialogContent
                className="management-dialog tenant-access-dialog max-w-xl"
                style={{ overscrollBehavior: "contain" }}
            >
                <DialogHeader className="management-dialog-header">
                    <div className="management-dialog-heading">
                        <span className="management-dialog-icon">
                            <Shield aria-hidden="true" />
                        </span>
                        <div>
                            <DialogTitle>
                                Request {osType === "linux" ? "Linux" : osType === "manual" ? "Manual Machine" : "Windows"} Access
                            </DialogTitle>
                            <p className="text-muted-foreground">
                                Configure access for <span className="font-medium text-foreground">{applicationName}</span>
                            </p>
                        </div>
                    </div>
                </DialogHeader>

                <div className="management-dialog-body">
                    {osType === "windows" && !device?.isManuallyAddedMachine && (
                        <section className="management-form-section">
                            <div className="management-section-title">
                                <Settings aria-hidden="true" />
                                Credentials
                                {checkingLocalUser && (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                                )}
                            </div>
                            <RadioGroup
                                value={connectionMode}
                                onValueChange={handleConnectionModeChange}
                                className="tenant-access-options"
                                data-columns="3"
                            >
                                <TooltipProvider delayDuration={200}>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <div
                                                className="relative"
                                                onClick={(e) => {
                                                    if (jitDisabled) {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }
                                                }}
                                            >
                                                <RadioGroupItem
                                                    value="generic"
                                                    id="generic"
                                                    className="peer sr-only"
                                                    disabled={jitDisabled}
                                                />
                                                <Label
                                                    htmlFor={jitDisabled ? undefined : "generic"}
                                                    aria-disabled={jitDisabled}
                                                    title={jitDisabled ? JIT_DISABLED_DISCLAIMER : undefined}
                                                    className="tenant-access-option"
                                                    data-layout="stacked"
                                                >
                                                    <User aria-hidden="true" />
                                                    <span>JIT User</span>
                                                </Label>
                                            </div>
                                        </TooltipTrigger>
                                        {jitDisabled && (
                                            <TooltipContent
                                                side="bottom"
                                                align="start"
                                                sideOffset={8}
                                                collisionPadding={16}
                                                className="z-[300] max-w-sm whitespace-normal break-words px-3 py-2 text-xs leading-relaxed"
                                            >
                                                {JIT_DISABLED_DISCLAIMER}
                                            </TooltipContent>
                                        )}
                                    </Tooltip>
                                </TooltipProvider>
                                <div
                                    className="relative"
                                    onClick={() => {
                                        setConnectionMode("existingCredentials");
                                        setStep("credentials");
                                    }}
                                >
                                    <RadioGroupItem value="existingCredentials" id="existingCredentials" className="peer sr-only" />
                                    <Label htmlFor="existingCredentials" className="tenant-access-option" data-layout="stacked">
                                        <Key aria-hidden="true" />
                                        <span>Configured</span>
                                    </Label>
                                </div>
                                <div className="relative">
                                    <RadioGroupItem value="manualCredentials" id="manualCredentials" className="peer sr-only" />
                                    <Label htmlFor="manualCredentials" className="tenant-access-option" data-layout="stacked">
                                        <FileText aria-hidden="true" />
                                        <span>Manual</span>
                                    </Label>
                                </div>
                            </RadioGroup>
                            {jitDisabled && (
                                <p className="tenant-access-note">{JIT_DISABLED_DISCLAIMER}</p>
                            )}
                        </section>
                    )}

                    <section className="management-form-section">
                        <div className="management-section-title">
                            <Clock aria-hidden="true" />
                            Access Type
                        </div>
                        <RadioGroup value={accessType} onValueChange={setAccessType} className="tenant-access-options">
                            <div className="relative">
                                <RadioGroupItem value="DYNAMIC" id="DYNAMIC" className="peer sr-only" />
                                <Label htmlFor="DYNAMIC" className="tenant-access-option">
                                    <Clock aria-hidden="true" data-tone="warning" />
                                    <span>Timed</span>
                                </Label>
                            </div>
                            <div className="relative">
                                <RadioGroupItem value="permanent" id="permanent" className="peer sr-only" />
                                <Label htmlFor="permanent" className="tenant-access-option">
                                    <CheckCircle2 aria-hidden="true" data-tone="success" />
                                    <span>Permanent</span>
                                </Label>
                            </div>
                        </RadioGroup>

                        <div className="tenant-access-summary">
                            <div>
                                <span>Application</span>
                                <strong>{applicationName}</strong>
                            </div>
                            <div>
                                <span>Computer</span>
                                <strong>{deviceName}</strong>
                            </div>
                            {connectionMode === "existingCredentials" && selectedCredential && (
                                <div data-wide="true">
                                    <span>Credential</span>
                                    <strong>{selectedCredential.name || selectedCredential.credentialUsername}</strong>
                                </div>
                            )}
                            {connectionMode === "manualCredentials" && (
                                <div data-wide="true">
                                    <span>Method</span>
                                    <strong>Manual Entry</strong>
                                </div>
                            )}
                        </div>

                        {accessType === "DYNAMIC" && (
                            <div className="tenant-access-switch">
                                <Label htmlFor="schedule-access">Schedule Access</Label>
                                <Switch
                                    id="schedule-access"
                                    checked={scheduleAccess}
                                    onCheckedChange={setScheduleAccess}
                                />
                            </div>
                        )}
                    </section>

                    <section className="management-form-section">
                        <div className="management-form-field">
                            <Label htmlFor="justification">Access Justification *</Label>
                            <Textarea
                                id="justification"
                                value={justification}
                                onChange={(e) => setJustification(e.target.value)}
                                placeholder="Provide business justification for this access request..."
                                data-testid="textarea-justification"
                            />
                        </div>

                        {accessType === "DYNAMIC" && !scheduleAccess && (
                            <div className="management-form-field">
                                <Label htmlFor="time-minutes">Duration (minutes) *</Label>
                                <Input
                                    id="time-minutes"
                                    value={timeMinutes}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === "") {
                                            setTimeMinutes("");
                                            return;
                                        }
                                        if (!/^\d+$/.test(value)) return;
                                        const num = Number(value);
                                        if (num >= 1 && num <= 999) {
                                            setTimeMinutes(value);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (["e", "E", "+", "-", "."].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    placeholder="e.g., 60"
                                    type="number"
                                    min="1"
                                    max="999"
                                    className="tenant-access-duration"
                                    data-testid="input-time-minutes"
                                />
                            </div>
                        )}

                        {scheduleAccess && (
                            <div className="management-form-field">
                                <Label>Schedule Window *</Label>
                                <div className="tenant-access-schedule">
                                    <div className="management-form-field">
                                        <Label htmlFor="start-date">Start</Label>
                                        <Input
                                            id="start-date"
                                            type="datetime-local"
                                            value={startDate}
                                            min={(() => {
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                return today.toISOString().slice(0, 16);
                                            })()}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                const selected = new Date(value);
                                                setEndDate("");

                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);

                                                if (selected < today) {
                                                    toast({
                                                        title: "Start Date/Time Selection Criteria",
                                                        description: "Start date/time cannot be before today.",
                                                        variant: "destructive",
                                                    });
                                                    return;
                                                }

                                                const maxAllowed = new Date();
                                                maxAllowed.setMonth(today.getMonth() + 3);

                                                if (selected > maxAllowed) {
                                                    toast({
                                                        title: "Start Date/Time Selection Criteria",
                                                        description: "Start date/time cannot be more than 3 months from today.",
                                                        variant: "destructive",
                                                    });
                                                    return;
                                                }

                                                setStartDate(value);

                                            }}
                                            className="appearance-auto"
                                            data-testid="input-start-date"
                                        />
                                    </div>

                                    <div className="management-form-field">
                                        <Label htmlFor="end-date">End</Label>
                                        <Input
                                            id="end-date"
                                            type="datetime-local"
                                            value={endDate}
                                            disabled={!startDate}
                                            min={startDate || new Date().toISOString().slice(0, 16)}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                const now = new Date();
                                                const end = new Date(value);
                                                const start = startDate ? new Date(startDate) : now;

                                                if (end <= start) {
                                                    toast({
                                                        title: "End Date/Time Selection Criteria",
                                                        description: "End date/time must be later than the start date/time.",
                                                        variant: "destructive",
                                                    });
                                                    return;
                                                }

                                                if (end < now) {
                                                    toast({
                                                        title: "End Date/Time Selection Criteria",
                                                        description: "End date/time cannot be in the past.",
                                                        variant: "destructive",
                                                    });
                                                    return;
                                                }

                                                const maxAllowed = new Date(start);
                                                maxAllowed.setDate(start.getDate() + 3);

                                                if (end > maxAllowed) {
                                                    toast({
                                                        title: "End Date/Time Selection Criteria",
                                                        description: "End date/time cannot be more than 3 days after the start date/time.",
                                                        variant: "destructive",
                                                    });
                                                    return;
                                                }

                                                setEndDate(value);
                                            }}

                                            className="appearance-auto"
                                            data-testid="input-end-date"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                </div>

                <div className="management-dialog-footer">
                    {connectionMode === "existingCredentials" && (
                        <Button variant="outline" onClick={handleBack} data-testid="button-back">
                            Back
                        </Button>
                    )}
                    <Button variant="outline" onClick={handleCancel} data-testid="button-cancel">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={
                            isSubmitting ||
                            !justification.trim() ||
                            (accessType === "DYNAMIC" &&
                                ((!scheduleAccess && !timeMinutes) ||
                                    (scheduleAccess && (!startDate || !endDate))))
                        }
                        data-testid="button-submit"
                        className="endpoint-primary-action"
                    >
                        {isSubmitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Shield className="w-4 h-4 mr-1" />}
                        {isSubmitting ? "Submitting..." : "Submit"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
